import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { DatasetEntryDto, IntentForm, IntentDto } from "@closerplatform/spinner-openapi";
import { observer } from "mobx-react";
import { useStore } from "../../../models";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { SubtitleDescription } from "../../settings/subtitleDescription";
import { EditInput } from "@fidget/common/components/form/editInput";
import { DeletableForm } from "../../settings/settingsForms/deletableForm/deleteableForm";
import { ChatInput, InputType } from "../../../models/room";
import { IChatInput } from "../../../models/room/ChatInput";
import { DiscardApplyButtons } from "../../settings/discardApplyButtons";
import { getNumericFormatTimestamp } from "@fidget/common/utils/timeFormatter";
import { SettingsForm } from "../../settings/settingsForm";
import commonStyles from "../../../containers/settings/settingsEditCommon.css";
import modelStyles from "./modelConfiguration.css";
import styles from "./intentConfiguration.css";
import { logger } from "@fidget/common/utils";
import { VerticalSeparator } from "../../settings/settingsForms/separators/verticalSeparator/verticalSeparator";
import { ChatMessageInput } from "../../../containers/chat/chatMessageInput";
import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import { replaceText, templatePairs } from "../../../../src/models/intent/intentStoreUtils";
import { staticConfig } from "@fidget/common/config/static-config";
import { useAgentServices } from "../../../models/agentServices";
import { HttpStatusCode } from "@fidget/common/utils/httpStatusCodes";

interface Props {
  onGoBackClicked: () => void;
  intentDto: IntentDto;
  updateIntent?: (dto: IntentDto) => void;
}
export const IntentConfiguration = observer((props: Props) => {
  const {
    intentDto,
    descriptionInput,
    setDescription,
    onSaveDescription,
    onMessageDelete,
    responseState,
    onSaveResponse,
    onDiscardResponse,
    removeMessageFromUi,
    entries,
    hasUnsavedChanges,
    onGoBack,
    responseModified,
    setResponseModified
  } = intentConfigurationState(props);

  const getDescriptionInputValue = () => {
    if (descriptionInput !== undefined) {
      return descriptionInput;
    }
    if (intentDto.intentDescription !== undefined) {
      return intentDto.intentDescription;
    }

    return "";
  };

  const renderTitle = () => (
    <div className={styles.title} onClick={onGoBack}>
      <div className={commonStyles.title}>
        <i className={classNames("icon-arrow", styles.rotated)} />
        <L10n id="ai.intentConfiguration.title" values={{ intentName: intentDto.intent }} />
      </div>
    </div>
  );

  const renderIntentDescriptionSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("ai.intentConfiguration.description.title")}
        description={intl.formatMessage("ai.intentConfiguration.description.description")}
      />
      <form
        onSubmit={(e) => e.preventDefault()}
        className={classNames(commonStyles.content, styles["form-with-buttons"])}
      >
        <div className={styles.input}>
          <EditInput
            value={getDescriptionInputValue()}
            onChange={(ev) => setDescription(ev.currentTarget.value)}
            placeholder={intl.formatMessage("ai.intentConfiguration.description.placeholder")}
            id="description_input"
          />
        </div>
        <DiscardApplyButtons
          onApply={onSaveDescription}
          onDiscard={() => setDescription(undefined)}
          hidden={descriptionInput === undefined}
        />
      </form>
    </div>
  );

  const renderIntentResponseSection = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("ai.intentConfiguration.response.title")}
        description={intl.formatMessage("ai.intentConfiguration.response.description")}
      />
      <div className={classNames(commonStyles.content, styles["form-with-buttons"])}>
        <div className={styles.input}>
          <ChatMessageInput
            inputChatState={responseState}
            onChange={() => setResponseModified(true)}
            placeholder={intl.formatMessage(
              "ai.modelConfiguration.intent.form.response.placeholder"
            )}
            activationSequence={staticConfig.mentionActivationSequence}
            delimeterSequence={staticConfig.mentionDelimeterSequence}
            hideFileUpload
          />
        </div>
        <DiscardApplyButtons
          onDiscard={onDiscardResponse}
          onApply={onSaveResponse}
          hidden={!responseModified}
        />
      </div>
    </div>
  );

  const renderMessagesDescription = () => (
    <div className={commonStyles.section}>
      <SubtitleDescription
        subtitle={intl.formatMessage("ai.intentConfiguration.messages.title")}
        description={intl.formatMessage("ai.intentConfiguration.messages.description")}
      />
    </div>
  );

  const renderMessageList = (loadedEntries: ReadonlyArray<DatasetEntryDto>) => {
    return loadedEntries.map((entry) => (
      <DeletableForm
        key={entry.id}
        onDelete={() => onMessageDelete(entry.id)}
        deleteDescriptionId="ai.intentConfiguration.message.areYouSure"
        deleteConfirmationId="ai.intentConfiguration.message.removed"
        removeFromUi={() => removeMessageFromUi(entry.id)}
      >
        <div className={modelStyles["intent-form"]}>
          <div className={classNames(styles["form-message-div"], modelStyles["form-div"])}>
            <p className={classNames(modelStyles["description-font"], modelStyles["stat-header"])}>
              {intl.formatMessage("ai.intentConfiguration.message.form.text")}
            </p>
            <p className={modelStyles["stat-content-bold"]}>{entry.text}</p>
          </div>
          <VerticalSeparator />
          <div className={modelStyles["form-div"]}>
            <p className={classNames(modelStyles["description-font"], modelStyles["stat-header"])}>
              {intl.formatMessage("ai.intentConfiguration.message.form.context")}
            </p>
            <p className={modelStyles["stat-content"]}>
              {entry.context ? entry.context.join(", ") : ""}
            </p>
          </div>
          <VerticalSeparator />
          <div className={modelStyles["form-div"]}>
            <p className={classNames(modelStyles["description-font"], modelStyles["stat-header"])}>
              {intl.formatMessage("ai.intentConfiguration.message.form.added")}
            </p>
            <p className={modelStyles["stat-content"]}>
              {getNumericFormatTimestamp(entry.updatedAt, intl.formatMessage("time.at"))}
            </p>
          </div>
        </div>
      </DeletableForm>
    ));
  };

  return (
    <SettingsForm withoutSaveDiscardButtons disabled={!hasUnsavedChanges()}>
      {renderTitle()}
      {renderIntentDescriptionSection()}
      {renderIntentResponseSection()}
      {renderMessagesDescription()}
      {entries ? renderMessageList(entries) : <CloserLoaderComponent />}
    </SettingsForm>
  );
});

interface State {
  intentDto: IntentDto;
  descriptionInput?: string;
  setDescription: (input?: string) => void;
  onSaveDescription: () => Promise<void>;
  onMessageDelete: (id: string) => Promise<void>;
  removeMessageFromUi: (id: string) => void;
  onSaveResponse: () => void;
  onDiscardResponse: () => void;
  responseState: IChatInput;
  entries?: ReadonlyArray<DatasetEntryDto>;
  hasUnsavedChanges: () => boolean;
  onGoBack: () => void;
  setResponseModified: (b: boolean) => void;
  responseModified: boolean;
}
const intentConfigurationState = (props: Props): State => {
  const { spinnerService } = useAgentServices();
  const { uiStore } = useStore();
  const [intentDto, setIntentDto] = useState<IntentDto>(props.intentDto);
  const [entries, setEntries] = useState<ReadonlyArray<DatasetEntryDto>>();
  const [descriptionInput, setDescription] = useState<string>();
  const [responseState] = useState<IChatInput>(() =>
    ChatInput.create({ activeInputType: InputType.Message, fileStore: {} })
  );
  const [responseModified, setResponseModified] = useState<boolean>(false);

  const loadEntries = async () => {
    try {
      const datasetEntries = await spinnerService.getDatasetEntries(props.intentDto.id);
      setEntries(datasetEntries);
    } catch (e) {
      if ((e as Response).status !== HttpStatusCode.NOT_FOUND) {
        logger.error({ message: "Couldn't load dataset entries", error: e });
        uiStore.addGenericErrorToast();
      }

      setEntries([]);
    }
  };

  useEffect(() => {
    loadEntries();
    responseState.fillFromIntentDto(props.intentDto);
    setIntentDto(props.intentDto);
  }, []);

  const onMessageDelete = (entryId: string) =>
    spinnerService.deleteDatasetEntry(props.intentDto.id, entryId);

  const removeMessageFromUi = (id: string) => {
    if (entries) {
      setEntries(entries.filter((e) => e.id !== id));
    }
  };

  const onSaveResponse = async () => {
    const replacedResponse = replaceText(responseState.inputText, templatePairs, true);

    const updatedIntent: IntentForm = {
      ...props.intentDto,
      response: replacedResponse,
      context: responseState.attachments.length > 0 ? responseState.attachments : undefined
    };

    try {
      const intent = await spinnerService.updateIntent(props.intentDto.id, updatedIntent);
      setResponseModified(false);
      setIntentDto(intent);
      if (props.updateIntent) {
        props.updateIntent(intent);
      }
    } catch (e) {
      logger.error({ message: "Couldn't save intent's response", error: e });
      uiStore.addGenericErrorToast();
    }
  };

  const onDiscardResponse = () => {
    responseState.clearMessageInput();
    responseState.fillFromIntentDto(props.intentDto);
    setResponseModified(false);
  };

  const onSaveDescription = async () => {
    const updatedIntent = { ...props.intentDto, intentDescription: descriptionInput };
    try {
      const intent = await spinnerService.updateIntent(props.intentDto.id, updatedIntent);
      setDescription(undefined);
      setIntentDto(intent);
      if (props.updateIntent) {
        props.updateIntent(intent);
      }
    } catch (e) {
      logger.error({ message: "Couldn't save intent's description", error: e });
      uiStore.addGenericErrorToast();
    }
  };

  const onGoBack = () => {
    if (hasUnsavedChanges()) {
      uiStore.addErrorToast(intl.formatMessage("settings.widgetConfig.unsavedChanges"));
      return;
    }

    props.onGoBackClicked();
  };

  const hasUnsavedChanges = (): boolean =>
    (!!descriptionInput && descriptionInput.length > 0) ||
    (responseState.isReadyToSend && responseModified);

  return {
    intentDto,
    descriptionInput,
    setDescription,
    onSaveDescription,
    onMessageDelete,
    onSaveResponse,
    onDiscardResponse,
    responseState,
    removeMessageFromUi,
    entries,
    hasUnsavedChanges,
    onGoBack,
    responseModified,
    setResponseModified
  };
};
